import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Headphones, Gavel, Clock, Flame } from "lucide-react";
import api, { API } from "../lib/api";
import ProductCard from "../components/ProductCard";
import FlashSaleSection from "../components/FlashSaleSection";

import { formatIDR } from "../lib/format";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

// Countdown mini untuk kartu lelang di beranda
function useCountdown(endsAt, isFrozen = false) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (isFrozen) return { text: "FROZEN", ended: false };
  const remaining = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(remaining / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const text =
    h >= 24
      ? `${Math.floor(h / 24)}h ${h % 24}j ${m}m`
      : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return { text, ended: remaining <= 0 };
}

function MiniCountdown({ endsAt, isFrozen }) {
  const { text, ended } = useCountdown(endsAt, isFrozen);
  return (
    <span className={`font-mono ${isFrozen ? "text-[#7C6EFF]" : ended ? "text-[#FF003C]" : "text-[#FFD700]"}`}>
      {ended ? "ENDED" : text}
    </span>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [topups, setTopups] = useState([]);
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, p, t] = await Promise.all([
        api.get("/categories"),
        api.get("/products?sort=popular&limit=10"),
        api.get("/products?sort=newest&limit=10"),
      ]);
      setCategories(c.data);
      setProducts(p.data);
      setTopups(t.data);
    })().catch(() => {});

    // Ambil lelang aktif (live/frozen) untuk section beranda
    (async () => {
      const { data } = await api.get("/auctions");
      setAuctions(data.filter((a) => ["live", "frozen"].includes(a.status)).slice(0, 12));
    })().catch(() => {});
  }, []);


  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-60" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1779758701721-5c9138ecb5f1?crop=entropy&cs=srgb&fm=jpg&w=1920)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(2px)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.85) 60%, #050505 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 sm:py-20 md:py-32 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-6">
              <span className="tag tag-accent">// USTORE × UNDEADSHOPP</span>
              <span className="tag">since 2017</span>
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] text-white">
              Naik Level.
              <br />
              <span className="text-[#00F0FF] neon-cyan">Tanpa Grind.</span>
            </h1>
            <p className="mt-4 md:mt-6 text-white/70 text-sm md:text-lg max-w-xl leading-relaxed">
              Pusat jual-beli item game digital #1 di Indonesia. Beli diamond, top-up, voucher & akun langka — proses hitungan detik, aman selamanya.
            </p>
            <div className="mt-6 md:mt-8 flex flex-wrap gap-2 md:gap-3">
              <Link to="/browse" data-testid="hero-cta-browse" className="btn-primary inline-flex items-center gap-2">
                Masuk Marketplace <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register" data-testid="hero-cta-sell" className="btn-ghost">
                Jadi Seller
              </Link>
              <a
                href="https://undeadshopp.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-topup"
                className="group relative inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-display font-black uppercase tracking-[0.15em] text-xs hover:bg-[#FFD700] transition-colors"
              >
                <span className="flex flex-col items-start leading-tight">
                  <span>TopUp</span>
                  <span className="flex items-center gap-1 text-[9px] font-semibold tracking-[0.2em] text-black/70 group-hover:text-black/80 normal-case">
                    <Zap className="w-2.5 h-2.5 fill-current" /> instan
                  </span>
                </span>
              </a>
              <Link
                to="/auction"
                data-testid="hero-cta-auction"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3D2BFF] text-white font-display font-black uppercase tracking-[0.15em] text-xs hover:bg-[#5C4DFF] transition-colors border border-[#3D2BFF] hover:border-[#5C4DFF]"
                style={{ boxShadow: "0 0 24px rgba(61, 43, 255, 0.4)" }}
              >
                <Gavel className="w-4 h-4" /> LELANG
              </Link>
            </div>
            <div className="mt-8 md:mt-12 grid grid-cols-3 gap-3 md:gap-4 max-w-lg">
              {[
                { n: "120K+", l: "Pemain Aktif" },
                { n: "98%", l: "Tingkat Berhasil" },
                { n: "24/7", l: "Live Trade" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#00F0FF] pl-2 md:pl-3">
                  <div className="font-display font-black text-lg md:text-2xl text-white">{s.n}</div>
                  <div className="text-[9px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-white/50 leading-tight">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative grid grid-cols-2 gap-3">
              {categories.slice(0, 4).map((c, i) => (
                <Link
                  key={c.id}
                  to={`/browse?category=${c.id}`}
                  data-testid={`hero-cat-${c.id}`}
                  className={`relative overflow-hidden border border-white/10 group ${i % 2 === 0 ? "translate-y-4" : ""}`}
                  style={{ aspectRatio: "1/1.2" }}
                >
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">// 0{i + 1}</span>
                    <h3 className="font-display font-bold text-white text-sm mt-1">{c.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Flash Sale — sengaja ditaruh tepat di bawah hero supaya jadi hal
          pertama yang dilihat buyer setelah masuk beranda. Section ini
          otomatis tidak dirender kalau tidak ada flash sale yang live. */}
      <FlashSaleSection />

      {/* Features strip */}
      <section className="border-b border-white/10">

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 md:py-6 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
          {[
            { icon: Zap, t: "Pengiriman Instan", d: "Item masuk hitungan detik" },
            { icon: Shield, t: "Dilindungi Escrow", d: "Dana cair setelah konfirmasi pembeli" },
            { icon: Headphones, t: "Customer Service 24/7", d: "Tim manusia asli, bukan bot" },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-3 md:gap-4">
              <div className="w-9 h-9 md:w-10 md:h-10 border border-[#00F0FF]/50 flex items-center justify-center shrink-0">
                <f.icon className="w-4 h-4 md:w-5 md:h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h4 className="font-display font-bold text-sm md:text-base text-white leading-tight">{f.t}</h4>
                <p className="text-xs md:text-sm text-white/55">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lelang Sedang Berlangsung */}
      {auctions.length > 0 && (
        <section
          className="relative border-b border-white/10 overflow-hidden"
          data-testid="home-live-auctions"
        >
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 20% 0%, rgba(61, 43, 255, 0.18) 0%, transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(124,110,255,0.12) 0%, transparent 55%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
            <div className="flex items-end justify-between mb-5 md:mb-8 flex-wrap gap-3">
              <div>
                <span
                  className="tag inline-flex items-center gap-1.5"
                  style={{ borderColor: "rgba(61,43,255,0.6)", color: "#7C6EFF", background: "rgba(61,43,255,0.12)" }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7C6EFF] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7C6EFF]" />
                  </span>
                  LIVE · {auctions.length} LOT
                </span>
                <h2 className="font-display font-black text-2xl md:text-5xl tracking-tighter text-white mt-2 flex items-center gap-3">
                  <Gavel className="w-6 h-6 md:w-9 md:h-9 text-[#7C6EFF]" />
                  Lelang Sedang Berlangsung
                </h2>
                <p className="mt-2 text-sm md:text-base text-white/60 max-w-xl">
                  Rebut akun langka & item eksklusif sebelum kehabisan. Bid pakai saldo — refund otomatis kalau kalah.
                </p>
              </div>
              <Link
                to="/auction"
                data-testid="home-auction-see-all"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#3D2BFF] hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-[0.15em] text-xs transition-colors"
                style={{ boxShadow: "0 0 24px rgba(61, 43, 255, 0.4)" }}
              >
                Lihat Semua Lelang <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Scroll horizontal */}
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory scroll-smooth home-auction-scroll">
              {auctions.map((a) => (
                <Link
                  key={a.id}
                  to={`/auctions/${a.id}`}
                  data-testid={`home-auction-card-${a.id}`}
                  className="group relative shrink-0 snap-start w-[240px] sm:w-[280px] border border-white/10 hover:border-[#7C6EFF]/70 bg-[#0a0a0a] transition-all duration-200 hover:-translate-y-1 overflow-hidden"
                  style={{ boxShadow: "0 0 0 rgba(124,110,255,0)" }}
                >
                  <div className="aspect-[16/11] bg-black overflow-hidden relative">
                    {a.image_url && (
                      <img
                        src={resolveImg(a.image_url)}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                    {/* Badge status + game */}
                    <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
                      {a.status === "frozen" ? (
                        <span className="tag" style={{ borderColor: "rgba(124,110,255,0.6)", color: "#7C6EFF", background: "rgba(124,110,255,0.15)" }}>
                          FROZEN
                        </span>
                      ) : (
                        <span className="tag inline-flex items-center gap-1" style={{ borderColor: "rgba(0,240,255,0.5)", color: "#00F0FF", background: "rgba(0,240,255,0.12)" }}>
                          <Flame className="w-3 h-3" /> LIVE
                        </span>
                      )}
                    </div>

                    {/* Countdown */}
                    <div className="absolute top-3 right-3">
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/90 bg-black/70 px-2 py-1 border border-white/15">
                        <Clock className="w-3 h-3 text-[#FFD700]" />
                        <MiniCountdown endsAt={a.ends_at} isFrozen={a.status === "frozen"} />
                      </span>
                    </div>

                    {/* Game name */}
                    <div className="absolute bottom-3 left-3">
                      <span className="tag tag-accent">{a.game_name}</span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-white text-sm line-clamp-2 min-h-[40px] group-hover:text-[#00F0FF] transition-colors">
                      {a.title}
                    </h3>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">
                          {a.current_bid_amount ? "Bid saat ini" : "Mulai dari"}
                        </div>
                        <div className="font-display font-black text-lg text-[#00F0FF] leading-tight">
                          {formatIDR(a.current_bid_amount || a.starting_bid)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Bids</div>
                        <div className="font-display font-black text-lg text-white leading-tight">{a.bid_count}</div>
                      </div>
                    </div>
                    <div className="mt-4 inline-flex w-full items-center justify-center gap-2 px-3 py-2.5 bg-[#3D2BFF] group-hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-[0.15em] text-[11px] transition-colors">
                      <Gavel className="w-3.5 h-3.5" /> Ikut Lelang
                    </div>
                  </div>
                </Link>
              ))}

              {/* Kartu CTA lihat semua di akhir scroll */}
              <Link
                to="/auction"
                data-testid="home-auction-card-more"
                className="shrink-0 snap-start w-[240px] sm:w-[280px] border border-dashed border-[#7C6EFF]/40 hover:border-[#7C6EFF] bg-[#7C6EFF]/5 hover:bg-[#7C6EFF]/10 transition-all flex flex-col items-center justify-center text-center p-6 gap-3"
              >
                <div className="w-12 h-12 border border-[#7C6EFF]/50 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-[#7C6EFF]" />
                </div>
                <div className="font-display font-black text-white uppercase tracking-widest text-sm">Lihat Semua Lelang</div>
                <div className="text-xs text-white/50">Masuk arena lelang lengkap</div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-end justify-between mb-5 md:mb-8">
          <div>
            <span className="tag tag-accent">// 01 / GAME</span>

            <h2 className="font-display font-black text-2xl md:text-5xl tracking-tighter text-white mt-2">
              Pilih medan pertempuranmu
            </h2>
          </div>
          <Link to="/browse" className="hidden md:block text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#00F0FF]">
            Lihat semua →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/browse?category=${c.id}`}
              data-testid={`category-card-${c.id}`}
              className="relative overflow-hidden border border-white/10 hover:border-[#00F0FF]/60 transition-all group aspect-[4/5]"
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute top-3 left-3">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">// 0{i + 1}</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="font-display font-bold text-white text-base md:text-lg leading-tight">{c.name}</h3>
                <div className="mt-2 inline-flex items-center text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] gap-1 group-hover:gap-2 transition-all">
                  Masuk <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-end justify-between mb-5 md:mb-8">
          <div>
            <span className="tag tag-accent">// 02 / TRENDING</span>
            <h2 className="font-display font-black text-2xl md:text-5xl tracking-tighter text-white mt-2">
              Paling dicari hari ini
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* New */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-end justify-between mb-5 md:mb-8">
          <div>
            <span className="tag tag-accent">// 03 / PRODUK BARU</span>
            <h2 className="font-display font-black text-2xl md:text-5xl tracking-tighter text-white mt-2">
              Baru di Ustore
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {topups.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
    </div>
  );
}
